/**
 * Schema-valid Deal Brief samples — one per `BriefMode`.
 *
 * Shared (zero React, zero Glaze) so the wizard preview
 * (`web/app-home/.../viewPreviewData.ts`), frontend render tests, and backend
 * fixtures all read the same three briefs instead of each hand-rolling a
 * payload that drifts from `DealBriefZ`. Guarded by
 * `__tests__/deal-brief-samples.test.ts`, which `safeParse`s each one.
 *
 * Account-level detail is realistic on purpose (internal repo); no real
 * person emails or phone numbers appear anywhere.
 */

import type { BriefMode, BriefSource, DealBrief } from '../schemas/deal-brief';

/**
 * Terse constructor for the `{ value, source }` leaf every brief field uses.
 * Generic over the source literal so narrowed fields (`risk_callout` is
 * `'Keystone' | 'Rule'`, `no_state_callout` is `'Rule'`) type-check.
 */
/**
 * A model-written line: tagged `Model`, carrying the distinct sources it
 * cited. `written(x)` with no cites is the "unsupported" case the UI flags.
 */
function written<T>(value: T, ...cites: BriefSource[]): { value: T; source: 'Model'; cites: BriefSource[] } {
  return { value, source: 'Model', cites };
}

function sourced<T, S extends BriefSource>(value: T, source: S): { value: T; source: S } {
  return { value, source };
}

const DEAL_SAMPLE: Extract<DealBrief, { mode: 'deal' }> = {
  schema_version: 1,
  mode: 'deal',
  meeting_type: 'Follow-up',
  reasons: [
    { code: 'open_opportunity', detail: 'Open SFDC opportunity in stage 1. Qualification' },
    { code: 'prior_meetings', detail: '3 prior Gong calls on this account' },
  ],
  overridden_by: null,
  header: {
    account_name: sourced('ImageTrend', 'SFDC'),
    meeting_title: sourced('ImageTrend × Clay — pricing & security review', 'Gong'),
    meeting_number: sourced(4, 'Gong'),
    stage: sourced('1. Qualification', 'SFDC'),
    amount: sourced(48000, 'SFDC'),
    when: sourced('Thu Sep 17 · 12:00 PM EDT · Zoom', 'Terra'),
    close_date: sourced('Dec 15, 2026', 'SFDC'),
    sfdc_link: sourced('https://clay.lightning.force.com/lightning/r/Opportunity/006Rt00000ImgTrn/view', 'SFDC'),
    gong_link: sourced('https://us-12345.app.gong.io/call?id=4872019365512', 'Gong'),
  },
  what_youre_walking_into: [
    written('Public-safety software vendor (fire/EMS records) evaluating Clay to replace a manual ZoomInfo + spreadsheet prospecting workflow.', 'Terra'),
    written('Champion is the RevOps lead; economic buyer (VP Sales) has not yet joined a call.', 'Keystone'),
    written('Security questionnaire was sent after call #3 and is the current blocker on a pilot start date.', 'Keystone'),
  ],
  since_last_time: [
    written('Security questionnaire returned by Clay on Tuesday; ImageTrend has not acknowledged receipt.', 'Terra'),
    written('Two new Clay signups from imagetrend.com domain in the last 7 days.', 'Clay'),
    written('Opportunity close date pushed from end of quarter to next quarter.', 'SFDC'),
  ],
  get_out_of_this_call: [
    {
      loop: sourced('Confirm the security questionnaire is sufficient or get the exact remaining objection.', 'Keystone'),
      ask: written('Confirm the security questionnaire closes the CISO objection.', 'Keystone'),
      why: written('It is the stated blocker on a pilot start date.', 'Keystone'),
      court: sourced('them', 'Keystone'),
      rank: sourced(1, 'Rule'),
      verbatim_quote: sourced("Our CISO won't sign off on a pilot until she's seen the SOC 2 bridge letter.", 'Gong'),
    },
    {
      loop: sourced('Get the VP Sales on the next call — no economic buyer has been present yet.', 'Keystone'),
      ask: written('Get the VP Sales onto the next call.', 'Keystone'),
      why: written('No economic buyer has attended; the close date already slipped once.', 'Keystone'),
      court: sourced('us', 'Keystone'),
      rank: sourced(2, 'Rule'),
      verbatim_quote: null,
    },
    {
      loop: sourced('Agree on the 3 pilot use cases and who owns each on their side.', 'Keystone'),
      ask: written('Agree the 3 pilot use cases and an owner for each.', 'Keystone'),
      why: written('Pilot scope is the gate to the budget conversation.', 'Keystone'),
      court: sourced('both', 'Keystone'),
      rank: sourced(3, 'Rule'),
      verbatim_quote: sourced("If we can prove out territory build for two reps we'll have the budget conversation.", 'Gong'),
    },
  ],
  kpis: [
    sourced({ label: 'strategy', value: 'Nurture' }, 'Keystone'),
    sourced({ label: 'ICP grade', value: 'B' }, 'SFDC'),
    sourced({ label: 'stakeholders', value: '3 on · 2 missing' }, 'SFDC'),
    sourced({ label: 'loops in our court', value: '2 of 3' }, 'Keystone'),
  ],
  classification: {
    best_guess: false,
    revenue_model: sourced('B2G SaaS subscription', 'Classifier'),
    how_they_make_money: sourced('Annual per-agency licences for fire, EMS and hospital records software sold to public-safety departments.', 'Classifier'),
    comparable: sourced(
      {
        name: 'ESO Solutions',
        detail: 'Closed-won last year; same public-safety records category and similar RevOps-led buying motion.',
        url: 'https://clay.lightning.force.com/lightning/r/Account/001Rt00000EsoSol/view',
      },
      'Ashley DB',
    ),
  },
  narrative_unavailable: false,
  risk_callout: sourced('Close date has slipped once and no economic buyer has attended — classic single-threaded risk.', 'Keystone'),
  signals: [
    written({ text: 'Hiring a Sales Development Manager', polarity: '+' }, 'Clay'),
    written({ text: 'CFO on consolidating GTM tooling', polarity: '-' }, 'Clay'),
  ],
  expands: {
    stakeholders: [
      sourced({ name: 'Dana Whitfield', title: 'Director, Revenue Operations', role: 'Champion', met: 3, not_on_invite: false, not_in_crm: false }, 'SFDC'),
      sourced({ name: 'Marcus Okafor', title: 'VP Sales', role: 'Economic buyer', met: 0, not_on_invite: true, not_in_crm: false }, 'Gong'),
      sourced({ name: 'Priya Natarajan', title: 'CISO', role: 'Decision maker', met: 0, not_on_invite: true, not_in_crm: true }, 'Gong'),
    ],
    timeline: [
      sourced({ date: '2026-09-10', kind: 'email', text: 'Clay vs ZoomInfo and 6sense one-pager (outbound)' }, 'Gong'),
      sourced({ date: '2026-08-26', kind: 'meeting', text: 'Call #3 — pricing walkthrough; security questionnaire requested' }, 'Gong'),
      sourced({ date: '2026-08-17', kind: 'meeting', text: 'Call #2 — live build of a territory table' }, 'Gong'),
      sourced({ date: '2026-08-10', kind: 'meeting', text: 'Call #1 — intro via inbound demo request' }, 'Gong'),
      sourced({ date: '2026-07-14', kind: 'signal', text: 'News: executive hire' }, 'Clay'),
    ],
    clay_angles: [
      sourced('Territory build for 2 pilot reps using firmographic + hiring signals.', 'Terra'),
      sourced('Replace ZoomInfo export → spreadsheet dedupe with a Clay waterfall.', 'Terra'),
    ],
    // From `salesforce_account.technologies_used` (SFDC, `;`-separated).
    tech_stack: [
      sourced('Salesforce', 'SFDC'),
      sourced('HubSpot', 'SFDC'),
      sourced('Snowflake', 'SFDC'),
      sourced('ZoomInfo', 'SFDC'),
      sourced('6Sense', 'SFDC'),
      sourced('Marketo', 'SFDC'),
    ],
  },
};

const DISCOVERY_SAMPLE: Extract<DealBrief, { mode: 'discovery' }> = {
  schema_version: 1,
  mode: 'discovery',
  meeting_type: 'Discovery',
  reasons: [
    { code: 'first_meeting', detail: 'First Gong call with this account' },
    { code: 'no_opportunity', detail: 'No open SFDC opportunity' },
  ],
  overridden_by: null,
  header: {
    account_name: sourced('Photoroom', 'SFDC'),
    meeting_title: sourced('Photoroom / Clay — intro & discovery', 'Gong'),
    meeting_number: sourced(1, 'Gong'),
    stage: null,
    amount: null,
    when: sourced('Thu Sep 24 · 12:00 PM EDT · Zoom', 'Terra'),
    close_date: null,
    sfdc_link: sourced('https://clay.lightning.force.com/lightning/r/Account/001Rt00000PhotoRm/view', 'SFDC'),
    gong_link: null,
  },
  what_we_already_know: [
    written('Paris-based AI photo-editing company; freemium consumer app plus a growing API business for e-commerce sellers.', 'Classifier'),
    written('Sales team is small (≈8 AEs) and was hired in the last 12 months to build an enterprise motion.', 'Clay'),
    written('Inbound via a product-led signup from their Head of Growth.', 'Terra'),
  ],
  in_their_words: [
    sourced("We're drowning in self-serve signups and have no idea which ones are actually companies worth calling.", 'Form'),
    sourced('Our reps spend the first hour of every day in LinkedIn tabs.', 'Form'),
  ],
  find_out: [
    {
      rank: sourced(1, 'Rule'),
      question: written('How do signups get routed to the sales team today, and who owns that routing?', 'Keystone'),
      why: written('Routing is where a PLG-to-sales motion leaks; the owner is the likely champion.', 'Keystone'),
      hypothesis: written('Likely a manual HubSpot list with no enrichment — Clay could score and route in-line.', 'Keystone'),
    },
    {
      rank: sourced(2, 'Rule'),
      question: written('What does the enterprise ICP look like — seller GMV, marketplace, or catalogue size?', 'Keystone'),
      why: written('Defines which signups are worth a rep’s hour.', 'Keystone'),
      hypothesis: written('Marketplace sellers on Shopify/Amazon with ≥1k SKUs; we can detect that from storefront data.', 'Keystone'),
    },
    {
      rank: sourced(3, 'Rule'),
      question: written('Is there budget owned by Growth or by Sales for tooling this year?', 'Keystone'),
      why: null,
      hypothesis: written('Growth owns the budget; sales leadership is the user, not the buyer.', 'Keystone'),
    },
  ],
  kpis: [
    sourced({ label: 'strategy', value: 'Upsell' }, 'Keystone'),
    sourced({ label: 'ICP grade', value: 'B' }, 'SFDC'),
    sourced({ label: 'stakeholders', value: '1 on · 1 missing' }, 'SFDC'),
  ],
  classification: {
    best_guess: false,
    revenue_model: sourced('Freemium consumer + usage-based API', 'Classifier'),
    how_they_make_money: sourced('Pro subscriptions on the consumer app and metered API calls from e-commerce platforms and marketplaces.', 'Classifier'),
    comparable: sourced(
      {
        name: 'Canva',
        detail: 'Design tool with a PLG motion that later layered an enterprise sales team on top of self-serve signups.',
        url: null,
      },
      'Ashley DB',
    ),
  },
  narrative_unavailable: false,
  risk_callout: sourced('No account state yet — everything above is inferred from the signup form and public data.', 'Rule'),
  signals: [
    written({ text: 'Series B in the last 6 months', polarity: '+' }, 'Clay'),
    written({ text: 'Hiring a Head of Sales Development', polarity: '+' }, 'Clay'),
  ],
  expands: {
    stakeholders: [
      sourced({ name: 'Léa Marchand', title: 'Head of Growth', role: 'Champion', met: 0, not_on_invite: false, not_in_crm: true }, 'Form'),
      sourced({ name: 'Tomás Herrera', title: 'VP Sales', role: 'Decision maker', met: null, not_on_invite: true, not_in_crm: true }, 'Clay'),
    ],
    timeline: [
      sourced({ date: '2026-09-15', kind: 'signal', text: 'Pricing page visit' }, 'Clay'),
      sourced({ date: '2026-09-08', kind: 'signal', text: 'Plan cap reached · Enterprise conversion candidate' }, 'Terra'),
      sourced({ date: '2026-07-22', kind: 'signal', text: 'Self-serve signup from photoroom.com domain' }, 'Clay'),
      sourced({ date: '2026-07-25', kind: 'email', text: 'Demo request form submitted' }, 'Form'),
    ],
    clay_angles: [
      sourced('Signup scoring and routing table keyed on company domain.', 'Terra'),
      sourced('Marketplace-seller lookalike list built from Shopify/Amazon storefront enrichment.', 'Terra'),
    ],
    // From `salesforce_account.technologies_used` (SFDC, `;`-separated).
    tech_stack: [
      sourced('HubSpot', 'SFDC'),
      sourced('Salesloft', 'SFDC'),
      sourced('Amplitude', 'SFDC'),
      sourced('Stripe', 'SFDC'),
      sourced('Snowflake', 'SFDC'),
    ],
  },
};

const INBOUND_SAMPLE: Extract<DealBrief, { mode: 'inbound' }> = {
  schema_version: 1,
  mode: 'inbound',
  meeting_type: 'Inbound demo',
  reasons: [
    { code: 'inbound_form', detail: 'Booked through the website demo form' },
    { code: 'no_prior_activity', detail: 'No prior Gong calls or SFDC opportunity' },
  ],
  overridden_by: null,
  header: {
    account_name: sourced('Shepper', 'SFDC'),
    meeting_title: sourced('Shepper — inbound demo', 'Form'),
    meeting_number: sourced(1, 'Gong'),
    stage: null,
    amount: null,
    when: sourced('Wed Aug 12 · 10:00 AM BST · Zoom', 'Terra'),
    close_date: null,
    sfdc_link: sourced('https://clay.lightning.force.com/lightning/r/Lead/00QRt00000ShepPr/view', 'SFDC'),
    gong_link: null,
  },
  no_state_callout: sourced('Terra has no account state for Shepper yet — this brief is built from the form submission and public data only.', 'Rule'),
  why_they_came_to_us: [
    written('Form: "Need to find retail ops leaders at grocery chains in the UK and Ireland."', 'Form'),
    written('Referred by a current Clay customer in the field-marketing space (self-reported on the form).', 'Form'),
    written('Visited the pricing page 4 times in the week before booking.', 'Clay'),
  ],
  // Model-written questions; the last cites nothing, which the UI flags as
  // unsupported (a question is allowed to be a hunch — the tag just says so).
  qualify: [
    written('Team size and who actually does the prospecting today.', 'Form'),
    written('Current data sources (LinkedIn Sales Navigator? Apollo?) and what is missing.', 'Form', 'SFDC'),
    written('Target list size per quarter and how fresh it needs to be.', 'Form'),
    written('Who signs — founder-led or is there a sales leader with budget?', 'SFDC'),
    written('Timeline: is there a campaign or event driving urgency?'),
  ],
  kpis: [
    sourced({ label: 'type', value: 'New inbound' }, 'Rule'),
    sourced({ label: 'first-party history', value: '3 weeks' }, 'Terra'),
    sourced({ label: 'ICP grade', value: 'C' }, 'SFDC'),
    sourced({ label: 'stakeholders', value: '1 contact · 0 known buyers' }, 'SFDC'),
  ],
  classification: {
    // Exercises the low-confidence arm: the classifier was under the floor,
    // so the UI reads "Best guess: …", the comparable is withheld (null) and
    // `expands.clay_angles` is empty — both hang off the model.
    best_guess: true,
    revenue_model: sourced('Marketplace / gig-economy services', 'Classifier'),
    how_they_make_money: sourced('Brands and retailers pay per completed in-store audit; a crowd of app-based "Shepherds" carry out the checks.', 'Classifier'),
    comparable: null,
  },
  narrative_unavailable: false,
  risk_callout: sourced('Small team and founder-led sales — watch for a use case that is really a one-off list pull.', 'Rule'),
  signals: [
    written({ text: 'UK grocery-chain partnership announced', polarity: '+' }, 'Clay'),
    written({ text: 'Two open BD roles in London', polarity: '+' }, 'Clay'),
  ],
  expands: {
    stakeholders: [
      sourced({ name: 'Oliver Bennett', title: 'Co-founder & CEO', role: 'Unknown', met: 0, not_on_invite: false, not_in_crm: true }, 'Form'),
      sourced({ name: 'Amara Osei', title: 'Head of Partnerships', role: null, met: null, not_on_invite: true, not_in_crm: true }, 'Clay'),
    ],
    timeline: [
      sourced({ date: '2026-08-10', kind: 'signal', text: 'Pricing page visit' }, 'Clay'),
      sourced({ date: '2026-08-05', kind: 'email', text: 'Anastasiia sends ICP / use-case deck' }, 'Gong'),
      sourced({ date: '2026-07-22', kind: 'email', text: 'Inbound demo request — "Clay Demo — Request to Meet"' }, 'Form'),
    ],
    // Withheld with the comparable while the model is a best guess.
    clay_angles: [],
    // From `salesforce_account.technologies_used` (SFDC, `;`-separated).
    tech_stack: [
      sourced('Salesforce', 'SFDC'),
      sourced('HubSpot', 'SFDC'),
    ],
  },
};

/**
 * Keyed by mode, each narrowed to its own arm of the `DealBrief` union (so
 * `DEAL_BRIEF_SAMPLES.deal.get_out_of_this_call` type-checks). Assignable to
 * `Readonly<Record<BriefMode, DealBrief>>` wherever the wide type is wanted.
 */
export type DealBriefSamples = { readonly [M in BriefMode]: Extract<DealBrief, { mode: M }> };

export const DEAL_BRIEF_SAMPLES: DealBriefSamples = Object.freeze({
  deal: DEAL_SAMPLE,
  discovery: DISCOVERY_SAMPLE,
  inbound: INBOUND_SAMPLE,
});

/** The sample as it travels in `DisplayField.value` — JSON-stringified. */
export function dealBriefSampleJson(mode: BriefMode): string {
  return JSON.stringify(DEAL_BRIEF_SAMPLES[mode]);
}
